<template>
  <div>
    <div v-if="(runs || []).length === 0" class="list-panel-empty">No runs yet.</div>

    <div v-else class="tableWrap">
      <table class="table" :class="{ compactAccount: hideAccountColumn }">
        <thead>
          <tr>
            <th style="width: 140px">Status</th>
            <th v-if="!hideAccountColumn" style="width: 220px">Account</th>
            <th style="width: 200px">Agent</th>
            <th style="width: 200px">Runtime</th>
            <th style="width: 210px">Type</th>
            <th style="width: 200px">Outputs</th>
            <th>Summary</th>
            <th v-if="showBudgets" style="width: 230px">Budgets</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in runs" :key="r.id" class="runRow" @click="$emit('openRun', r)">
            <td>
              <div style="display:flex; gap:6px; align-items:center;">
                <span class="badge" :class="statusClass(r.status)">{{ r.status }}</span>
                <span v-if="r.test_mode" class="badge test">Test</span>
              </div>
            </td>
            <td v-if="!hideAccountColumn">
              <div class="accountCell">
                <FavIcon :domain="r.account_domain" :size="16" />
                <span class="accountDomain">{{ r.account_domain }}</span>
              </div>
            </td>
            <td>
              <div class="agentCell">
                <HeroIcon
                  v-if="r.agent_icon_name"
                  :name="r.agent_icon_name"
                  :iconStyle="r.agent_icon_style"
                  :color="r.agent_icon_color"
                  :size="16"
                />
                <HeroIcon v-else name="UserCircleIcon" iconStyle="outline" :size="16" />
                <span class="agentName">{{ r.runner_label || r.agent_name }}</span>
                <span v-if="!r.runner_label && Number.isFinite(Number(r.agent_version))" class="muted" style="font-size:12px;">v{{ Number(r.agent_version) }}</span>
              </div>
            </td>
            <td class="muted">
              <div class="createdCell">
                <div>{{ formatTs(r.created_at) }}</div>
                <div v-if="formatRuntimeHuman(r)" class="runtime">{{ formatRuntimeHuman(r) }}</div>
              </div>
            </td>
            <td class="muted" style="font-size:12px;">
              <div class="triggerLines">
                <div>
                  <span class="pill">{{ formatRunSourcePill(r) }}</span>
                </div>
              </div>
            </td>
            <td class="muted" style="font-size:12px;">
              <div class="outputPills">
                <div v-if="Number(r.reachout_count || 0) > 0" class="pillOut pillReachout">
                  <AppIcon name="reachout" :size="14" color="#2563eb" />
                  <span>Reachouts {{ Number(r.reachout_count || 0) }}</span>
                </div>
                <div v-if="Number(r.signal_count || 0) > 0" class="pillOut pillSignal">
                  <AppIcon name="signal" :size="14" color="#d97706" />
                  <span>Signals {{ Number(r.signal_count || 0) }}</span>
                </div>
                <div
                  v-if="Number(r.reachout_count || 0) === 0 && Number(r.signal_count || 0) === 0"
                  class="muted"
                >
                  —
                </div>
              </div>
            </td>
            <td>
              <div class="summaryText">{{ r.status_summary || r.pursued_goal }}</div>
            </td>
            <td v-if="showBudgets" class="muted" style="font-size:12px;">
              <div class="usageLines">
                <div>
                  Tools {{ r.tool_calls_used }}/{{ r.max_tool_calls }} · LeadIQ q {{ r.leadiq_query_count }}/{{ r.max_leadiq_queries }}
                </div>
                <div>
                  OpenAI ${{ formatUsd(r.openai_cost_usd) }} · {{ formatUc(r.openai_cost_uc) }} UC · calls {{ r.openai_call_count }} · tok {{ fmtInt(r.openai_prompt_tokens) }}/{{ fmtInt(r.openai_completion_tokens) }} · cache {{ fmtInt(r.openai_cached_input_tokens) }}/{{ fmtInt(r.openai_cache_write_input_tokens) }}
                </div>
                <div v-if="r.anthropic_call_count > 0">
                  Anthropic ${{ formatUsd(r.anthropic_cost_usd) }} · {{ formatUc(r.anthropic_cost_uc) }} UC · calls {{ r.anthropic_call_count }} · tok {{ fmtInt(r.anthropic_prompt_tokens) }}/{{ fmtInt(r.anthropic_completion_tokens) }} · cache {{ fmtInt(r.anthropic_cached_input_tokens) }}/{{ fmtInt(r.anthropic_cache_write_input_tokens) }}
                </div>
                <div>
                  Salesforce calls {{ r.salesforce_call_count }} · LeadIQ calls {{ r.leadiq_call_count }} · {{ formatUc(r.leadiq_cost_uc) }} UC
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="nextOffset" class="more">
      <button class="btn btn-secondary" :disabled="loading || !nextOffset" @click.stop="$emit('loadMore')">Load more</button>
    </div>
  </div>
</template>

<script setup>
import AppIcon from './AppIcon.vue'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'

const props = defineProps({
  runs: { type: Array, default: () => [] },
  nextOffset: { type: [Number, String, null], default: null },
  loading: { type: Boolean, default: false },
  showBudgets: { type: Boolean, default: true },
  hideAccountColumn: { type: Boolean, default: false },
  displayTz: { type: String, default: 'America/Los_Angeles' },
})

defineEmits(['openRun', 'loadMore'])

function statusClass(status) {
  if (status === 'succeeded') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'running') return 'warning'
  if (status === 'queued') return 'warning'
  return ''
}

function formatTs(ts) {
  try {
    return new Date(ts).toLocaleString(undefined, { timeZone: props.displayTz })
  } catch {
    return ts
  }
}

function formatRuntimeHuman(r) {
  const rawMs = r?.runtime_ms
  let ms = (rawMs === null || rawMs === undefined || rawMs === '') ? NaN : Number(rawMs)
  if (!Number.isFinite(ms)) {
    const start = Date.parse(r?.started_at)
    const end = Date.parse(r?.finished_at)
    if (Number.isFinite(start) && Number.isFinite(end)) {
      ms = end - start
    } else if (Number.isFinite(start) && String(r?.status || '').toLowerCase() === 'running') {
      ms = Date.now() - start
    }
  }
  if (!Number.isFinite(ms) || ms < 0) return ''

  let s = Math.round(ms / 1000)
  const h = Math.floor(s / 3600)
  s -= h * 3600
  const m = Math.floor(s / 60)
  s -= m * 60

  const parts = []
  if (h > 0) parts.push(`${h} hour${h === 1 ? '' : 's'}`)
  if (m > 0) parts.push(`${m} minute${m === 1 ? '' : 's'}`)
  if (s > 0 || parts.length === 0) parts.push(`${s} second${s === 1 ? '' : 's'}`)
  return parts.join(', ')
}

function isSignalRun(r) {
  const src = String(r?.run_source || '').trim().toLowerCase()
  return src === 'trigger_signal' || Boolean(r?.trigger_signal_id)
}

function formatRunSourcePill(r) {
  if (isSignalRun(r)) return 'trigger.signal'
  const mode = r?.execution_mode || ''
  if (mode === 'source') return 'find'
  if (mode === 'filter') return 'qualify'
  if (mode === 'prospect') return 'prospect'
  if (mode === 'sequence') return 'sequence'
  if (mode === 'revision') return 'revision'
  return r?.run_source || 'manual'
}

function formatUsd(n) {
  const x = Number(n || 0)
  return x.toFixed(2)
}

function fmtInt(n) {
  const x = Number(n || 0)
  if (!Number.isFinite(x)) return '0'
  return String(Math.trunc(x))
}

function formatUc(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
}
</script>

<style scoped>
.muted { color: var(--muted); }


.tableWrap {
  overflow-x: auto;
  width: 100%;
}

.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1180px;
}

.table.compactAccount {
  min-width: 960px;
}

.table th {
  text-align: left;
  font-size: 12px;
  color: var(--muted);
  font-weight: 700;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
}

.table td {
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.summaryText {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  max-width: 520px;
}

.runRow {
  cursor: pointer;
}

.runRow:hover {
  background: var(--row-hover-bg);
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 12px;
}

.badge.success {
  border-color: rgba(34, 197, 94, 0.35);
  background: rgba(34, 197, 94, 0.12);
  color: #166534;
}

.badge.danger {
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.10);
  color: #991b1b;
}

.badge.warning {
  border-color: rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #92400e;
}

.badge.test {
  border-color: rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
}


.agentCell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.agentName {
  font-weight: 600;
}

.accountCell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.accountDomain {
  font-weight: 600;
}

.createdCell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.runtime {
  font-size: 12px;
  color: var(--muted);
}

.outputPills {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pillOut {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  line-height: 1;
}

.pillReachout {
  border-color: rgba(37, 99, 235, 0.25);
  background: rgba(37, 99, 235, 0.06);
}

.pillSignal {
  border-color: rgba(217, 119, 6, 0.25);
  background: rgba(217, 119, 6, 0.10);
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 12px;
  background: var(--surface);
}

.usageLines {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
}

.more {
  padding: 12px 14px 0;
  display: flex;
  justify-content: center;
}
</style>
